import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { useTranslations } from "use-intl";

import { Skeleton } from "@lawline/ui/components/skeleton";

import { formatDate, formatDateTime } from "@platform/fmt";
import { WebhookDeliveriesTable } from "./components/webhook-deliveries-table";
import { WebhookEndpointActions } from "./components/webhook-endpoint-actions";
import { WebhookHealthBadge } from "./components/webhook-health-badge";
import { webhookEndpointsQueryOptions } from "./webhooks.queries";
import type { WebhookEndpoint } from "./webhooks.types";
import { endpointHealth, rotationEndsAt } from "./webhooks.utils";

export function WebhookEndpointPage() {
	const t = useTranslations("webhooks");
	const { endpointId } = useParams({ from: "/shell/webhooks/$endpointId" });
	const { data: endpoints, isPending, error } = useQuery(webhookEndpointsQueryOptions);
	const endpoint = endpoints?.find((row) => row.id === endpointId);

	return (
		<section className="mx-auto mt-2 flex w-full max-w-4xl flex-col gap-6">
			<Link
				to="/webhooks"
				className="text-muted-foreground hover:text-foreground flex w-fit items-center gap-1 text-sm"
			>
				<ArrowLeft className="size-4" />
				{t("endpoint.back")}
			</Link>

			{isPending ? (
				<Skeleton className="h-32 w-full" />
			) : error ? (
				<p role="alert" className="text-destructive text-sm">
					{t("error")}
				</p>
			) : !endpoint ? (
				<p className="text-muted-foreground text-sm">{t("endpoint.notFound")}</p>
			) : (
				<>
					<EndpointSummary endpoint={endpoint} />
					<WebhookEndpointActions endpoint={endpoint} />
					<WebhookDeliveriesTable endpoint={endpoint} />
				</>
			)}
		</section>
	);
}

function EndpointSummary({ endpoint }: { endpoint: WebhookEndpoint }) {
	const t = useTranslations("webhooks.endpoint");
	const tKinds = useTranslations("webhooks.kinds");
	const health = endpointHealth(endpoint);
	const rotation = rotationEndsAt(endpoint);

	return (
		<div className="flex flex-col gap-3">
			<div className="flex flex-wrap items-center gap-2">
				<h1 className="min-w-0 font-mono text-lg font-semibold break-all">{endpoint.url}</h1>
				<WebhookHealthBadge endpoint={endpoint} />
			</div>
			{endpoint.description && (
				<p className="text-muted-foreground text-sm">{endpoint.description}</p>
			)}
			<dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
				<dt className="text-muted-foreground">{t("eventsLabel")}</dt>
				<dd className="font-mono text-xs">{endpoint.eventTypes.join(", ")}</dd>
				<dt className="text-muted-foreground">{t("kindsLabel")}</dt>
				<dd>
					{endpoint.runKinds
						? endpoint.runKinds.map((kind) => tKinds(kind)).join(", ")
						: t("allKinds")}
				</dd>
				<dt className="text-muted-foreground">{t("createdLabel")}</dt>
				<dd>{formatDate(endpoint.createdAt)}</dd>
				<dt className="text-muted-foreground">{t("lastSuccessLabel")}</dt>
				<dd>{formatDateTime(endpoint.lastSuccessAt)}</dd>
				<dt className="text-muted-foreground">{t("lastFailureLabel")}</dt>
				<dd>{formatDateTime(endpoint.lastFailureAt)}</dd>
			</dl>
			{rotation && (
				<p role="status" className="text-sm">
					{t("rotationNotice", { time: formatDateTime(rotation) })}
				</p>
			)}
			{health === "gone" && <p className="text-destructive text-sm">{t("goneNotice")}</p>}
			{health === "disabledFailing" && (
				<p className="text-destructive text-sm">{t("disabledFailingNotice")}</p>
			)}
			{health === "failing" && (
				<p className="text-sm text-amber-700 dark:text-amber-400">
					{t("failingNotice", { time: formatDateTime(endpoint.failingSince) })}
				</p>
			)}
		</div>
	);
}
